/*
 * Meal times per home (wave 3, 30 Sep 2026) — pure helpers, no React.
 * Mirrors the API's `app/Support/MealSittings.php` and the app's
 * `App/Utils/mealSittings.js` (contract ~/Sites/.brand/meal-times-list-contract.md §1).
 *
 * A home's list lives in `location_setting.meal_sittings`. Empty / missing ⇒
 * DEFAULT_SITTINGS, which is exactly today's behaviour. Every home has one
 * Breakfast, one Lunch and one Dinner KIND (whatever it calls them, and they may
 * be switched off) plus any number of snack rounds.
 */

export const MAIN_KINDS = ["breakfast", "lunch", "dinner"];
export const SNACK_KIND = "snack";
export const KINDS = [...MAIN_KINDS, SNACK_KIND];

export const KIND_LABELS = {
  breakfast: "Breakfast",
  lunch: "Lunch",
  dinner: "Dinner",
  snack: "Snack",
};

export const LABEL_MAX = 30;
export const GRACE_MAX = 180;
export const DEFAULT_GRACE = 30;

export const DEFAULT_SITTINGS = Object.freeze([
  Object.freeze({
    key: "breakfast",
    kind: "breakfast",
    label: "Breakfast",
    enabled: true,
    from: "07:00",
    to: "09:30",
    orders_close: null,
    grace_min: 30,
  }),
  Object.freeze({
    key: "lunch",
    kind: "lunch",
    label: "Lunch",
    enabled: true,
    from: "12:00",
    to: "14:00",
    orders_close: "11:00",
    grace_min: 30,
  }),
  Object.freeze({
    key: "dinner",
    kind: "dinner",
    label: "Dinner",
    enabled: true,
    from: "17:00",
    to: "19:30",
    orders_close: "16:00",
    grace_min: 30,
  }),
]);

export const cloneDefaults = () => DEFAULT_SITTINGS.map((s) => ({ ...s }));

const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

/** True for a strict 24h "HH:MM". */
export const isTime = (value) =>
  typeof value === "string" && TIME_RE.test(value);

/** "7:5" / "07:05:00" / "07:05" → "07:05"; anything else → null. */
export const cleanTime = (value) => {
  if (typeof value !== "string") return null;
  const m = value.trim().match(/^(\d{1,2}):(\d{1,2})(?::\d{1,2})?$/);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
};

/** "12:30" → 750; invalid → null. */
export const toMinutes = (value) => {
  if (!isTime(value)) return null;
  const [h, m] = value.split(":").map(Number);
  return h * 60 + m;
};

/** 750 → "12:30" (wraps within the day). */
export const fromMinutes = (minutes) => {
  const total = ((Math.round(minutes) % 1440) + 1440) % 1440;
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

/** Minutes since midnight for a Date (defaults to now). */
export const minutesOfDay = (date = new Date()) =>
  date.getHours() * 60 + date.getMinutes();

export const isMain = (sitting) => MAIN_KINDS.includes(sitting?.kind);
export const isSnack = (sitting) => sitting?.kind === SNACK_KIND;

const toGrace = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n) : null;
};

const byFrom = (a, b) => {
  const fa = toMinutes(a.from);
  const fb = toMinutes(b.from);
  if (fa === null && fb === null) return 0;
  if (fa === null) return 1;
  if (fb === null) return -1;
  return fa - fb;
};

/** Sort a list by `from` (stable; unparseable times last). */
export const sortSittings = (list) =>
  (Array.isArray(list) ? [...list] : []).sort(byFrom);

/**
 * Tolerant read of whatever the API (or an older one) sends. Never throws.
 * Missing / empty / not an array ⇒ DEFAULT_SITTINGS. A main kind the payload
 * lacks is filled from the defaults so every screen can still find it.
 */
export const normaliseSittings = (raw) => {
  if (!Array.isArray(raw) || raw.length === 0) return cloneDefaults();
  const seenMain = new Set();
  const out = [];
  raw.forEach((item, index) => {
    if (!item || typeof item !== "object") return;
    const kind = KINDS.includes(item.kind) ? item.kind : null;
    if (!kind) return;
    if (kind !== SNACK_KIND) {
      if (seenMain.has(kind)) return;
      seenMain.add(kind);
    }
    const label =
      typeof item.label === "string" && item.label.trim()
        ? item.label.trim()
        : KIND_LABELS[kind];
    const snack = kind === SNACK_KIND;
    out.push({
      key:
        typeof item.key === "string" && item.key
          ? item.key
          : snack
            ? `snack_${index + 1}`
            : kind,
      kind,
      label,
      enabled: item.enabled !== false,
      from: cleanTime(item.from) || "",
      to: cleanTime(item.to) || "",
      orders_close: snack ? null : cleanTime(item.orders_close),
      // Main sittings always have a grace (the API 422s a null one);
      // only snack rounds are never flagged.
      grace_min: snack ? null : (toGrace(item.grace_min) ?? DEFAULT_GRACE),
    });
  });
  MAIN_KINDS.forEach((kind) => {
    if (!seenMain.has(kind)) {
      out.push({ ...DEFAULT_SITTINGS.find((s) => s.kind === kind) });
    }
  });
  return sortSittings(out);
};

/** The three main sittings (enabled or not), sorted by `from`. */
export const mains = (sittings) => sortSittings(sittings).filter(isMain);

/** Enabled main sittings, sorted by `from`. */
export const enabledMains = (sittings) =>
  mains(sittings).filter((s) => s.enabled);

/** `[fromMin, toMin]` or null when the times don't parse. */
export const windowOf = (sitting) => {
  const from = toMinutes(sitting?.from);
  const to = toMinutes(sitting?.to);
  if (from === null || to === null) return null;
  return [from, to];
};

/**
 * Minute of day after which "not recorded" may start: `to + grace`.
 * Snack rounds return null (never flagged). A main sitting always has a
 * grace; a missing one counts as the default +30 min.
 */
export const recordBy = (sitting) => {
  if (!sitting || isSnack(sitting)) return null;
  const w = windowOf(sitting);
  if (!w) return null;
  return w[1] + (toGrace(sitting.grace_min) ?? DEFAULT_GRACE);
};

/**
 * Default tab at minute `t`: the first enabled main sitting whose record_by
 * hasn't passed, else the last one. Replaces the old "<11 / <16" rule.
 */
export const defaultTab = (sittings, t = minutesOfDay()) => {
  const list = enabledMains(sittings);
  if (!list.length) return "lunch";
  const open = list.find((s) => {
    const w = windowOf(s);
    if (!w) return false;
    const by = recordBy(s);
    return t <= (by === null ? w[1] : by);
  });
  return (open || list[list.length - 1]).kind;
};

/** The home's name for a kind ("dinner" → "Tea"); falls back to the standard word. */
export const labelFor = (sittings, kind) => {
  const key = kind === "snacks" ? SNACK_KIND : kind;
  const hit = (Array.isArray(sittings) ? sittings : []).find(
    (s) => s?.kind === key && s.kind !== SNACK_KIND,
  );
  if (hit?.label) return hit.label;
  if (kind === "snacks") return "Snacks";
  return KIND_LABELS[key] || kind || "";
};

/**
 * Service tabs for the kitchen boards: the home's enabled main sittings, as
 * `{ key: kind, label }` (the order service is the kind). Falls back to all
 * three when a home has switched every main sitting off.
 */
export const serviceTabs = (sittings) => {
  const list = enabledMains(sittings);
  const use = list.length ? list : mains(sittings);
  return use.map((s) => ({
    key: s.kind,
    label: s.label || KIND_LABELS[s.kind],
  }));
};

/* ------------------------------------------------------------------ */
/* Validation — mirrors the API's PUT rules so the page can say it first. */

/**
 * @returns {{ valid: boolean, rows: Object<number, Object<string,string>>, general: string[] }}
 * `rows` is keyed by index in the list as given; field keys are
 * label / from / to / orders_close / grace_min.
 */
export const validateSittings = (list) => {
  const rows = {};
  const general = [];
  const setErr = (index, field, message) => {
    rows[index] = rows[index] || {};
    if (!rows[index][field]) rows[index][field] = message;
  };
  const items = Array.isArray(list) ? list : [];

  MAIN_KINDS.forEach((kind) => {
    const n = items.filter((s) => s?.kind === kind).length;
    if (n !== 1) {
      general.push(
        `Each home needs exactly one ${KIND_LABELS[kind]} sitting (it can be switched off).`,
      );
    }
  });
  items.forEach((s) => {
    if (!KINDS.includes(s?.kind)) general.push("Unknown sitting kind.");
  });

  const keys = items.map((s) => s?.key);
  if (keys.some((k) => !k) || new Set(keys).size !== keys.length) {
    general.push("Two sittings share the same key. Reload and try again.");
  }

  items.forEach((s, i) => {
    const label = typeof s?.label === "string" ? s.label.trim() : "";
    if (!label) setErr(i, "label", "Give it a name staff will see.");
    else if (label.length > LABEL_MAX)
      setErr(i, "label", `Keep it to ${LABEL_MAX} characters or fewer.`);

    const from = toMinutes(s?.from);
    const to = toMinutes(s?.to);
    if (from === null) setErr(i, "from", "Use a time like 12:30.");
    if (to === null) setErr(i, "to", "Use a time like 14:00.");
    if (from !== null && to !== null && from >= to) {
      setErr(i, "to", "Must end after it starts (no overnight sittings).");
    }

    if (isSnack(s)) {
      if (s.orders_close !== null && s.orders_close !== undefined) {
        setErr(i, "orders_close", "Snack rounds don't take orders.");
      }
      if (s.grace_min !== null && s.grace_min !== undefined) {
        setErr(i, "grace_min", "Snack rounds are never flagged.");
      }
      return;
    }

    if (s?.orders_close !== null && s?.orders_close !== undefined) {
      const close = toMinutes(s.orders_close);
      if (close === null) setErr(i, "orders_close", "Use a time like 11:00.");
      else if (from !== null && close >= from) {
        setErr(i, "orders_close", `Must be before it's served (${s.from}).`);
      }
    }

    // Main sittings are always flagged: 0–180 min, never "never".
    const g =
      s?.grace_min === null || s?.grace_min === undefined || s?.grace_min === ""
        ? NaN
        : Number(s.grace_min);
    if (!Number.isInteger(g) || g < 0 || g > GRACE_MAX) {
      setErr(i, "grace_min", `Between 0 and ${GRACE_MAX} minutes.`);
    }
  });

  // Enabled main sittings must not overlap each other (touching is fine).
  const live = items
    .map((s, i) => ({ s, i, w: windowOf(s) }))
    .filter((x) => isMain(x.s) && x.s.enabled && x.w && x.w[0] < x.w[1]);
  live.forEach((a) => {
    live.forEach((b) => {
      if (a.i === b.i) return;
      const overlaps = a.w[0] < b.w[1] && b.w[0] < a.w[1];
      // Flag the one that starts later (or the second of an exact tie).
      const later = a.w[0] > b.w[0] || (a.w[0] === b.w[0] && a.i > b.i);
      if (overlaps && later) {
        setErr(
          a.i,
          "from",
          `Overlaps ${b.s.label || KIND_LABELS[b.s.kind]} (${b.s.from}–${b.s.to}).`,
        );
      }
    });
  });

  return {
    valid: general.length === 0 && Object.keys(rows).length === 0,
    rows,
    general,
  };
};

/**
 * What the page PUTs: trimmed labels, snack rounds with no orders/grace,
 * sorted by `from`. Strips any client-only fields.
 */
export const serialiseSittings = (list) =>
  sortSittings(list).map((s) => {
    const snack = isSnack(s);
    return {
      key: s.key,
      kind: s.kind,
      label: typeof s.label === "string" ? s.label.trim() : "",
      enabled: s.enabled !== false,
      from: s.from,
      to: s.to,
      orders_close: snack ? null : s.orders_close || null,
      grace_min: snack ? null : (toGrace(s.grace_min) ?? DEFAULT_GRACE),
    };
  });

/** Structural equality of two lists as they would be saved. */
export const sameSittings = (a, b) =>
  JSON.stringify(serialiseSittings(a)) === JSON.stringify(serialiseSittings(b));

/* ------------------------------------------------------------------ */
/* Snack rounds */

const SNACK_SUGGESTIONS = [
  { label: "Elevenses", from: "10:30", to: "11:00" },
  { label: "Afternoon tea", from: "15:00", to: "15:30" },
  { label: "Supper", from: "19:30", to: "20:00" },
  { label: "Late snack", from: "20:30", to: "21:00" },
];

/** Next free `snack_N` key. */
export const nextSnackKey = (list) => {
  const used = (Array.isArray(list) ? list : [])
    .map((s) => /^snack_(\d+)$/.exec(s?.key || ""))
    .filter(Boolean)
    .map((m) => Number(m[1]));
  return `snack_${used.length ? Math.max(...used) + 1 : 1}`;
};

/** A new snack round, placed in the first suggested slot nothing else uses. */
export const newSnackRound = (list) => {
  const items = Array.isArray(list) ? list : [];
  const busy = items
    .filter((s) => s?.enabled !== false)
    .map(windowOf)
    .filter(Boolean);
  const pick =
    SNACK_SUGGESTIONS.find((slot) => {
      const f = toMinutes(slot.from);
      const t = toMinutes(slot.to);
      return !busy.some(([a, b]) => f < b && a < t);
    }) || SNACK_SUGGESTIONS[0];
  return {
    key: nextSnackKey(items),
    kind: SNACK_KIND,
    label: pick.label,
    enabled: true,
    from: pick.from,
    to: pick.to,
    orders_close: null,
    grace_min: null,
  };
};

/* ------------------------------------------------------------------ */
/* Day strip geometry (06:00–21:00 by default) */

export const STRIP_START = 6 * 60;
export const STRIP_END = 21 * 60;

const pct = (min, start, end) =>
  Math.round(((min - start) / (end - start)) * 10000) / 100;

/** Hour ticks every 3 hours: [{ label: "06:00", leftPct: 0 }, …]. */
export const dayStripTicks = (
  start = STRIP_START,
  end = STRIP_END,
  step = 180,
) => {
  const out = [];
  for (let m = start; m <= end; m += step) {
    out.push({ label: fromMinutes(m), leftPct: pct(m, start, end) });
  }
  return out;
};

/**
 * Blocks for the enabled sittings, clamped to the strip. Anything with
 * unparseable times, from ≥ to, or entirely outside the strip is left out.
 * `clash` marks an enabled main sitting that overlaps another one.
 */
export const dayStripBlocks = (list, start = STRIP_START, end = STRIP_END) => {
  const items = Array.isArray(list) ? list : [];
  const live = items
    .map((s, index) => ({ s, index, w: windowOf(s) }))
    .filter((x) => x.s?.enabled !== false && x.w && x.w[0] < x.w[1]);
  return live
    .map(({ s, index, w }) => {
      const from = Math.max(w[0], start);
      const to = Math.min(w[1], end);
      if (to <= from) return null;
      const clash =
        isMain(s) &&
        live.some(
          (o) =>
            o.index !== index && isMain(o.s) && w[0] < o.w[1] && o.w[0] < w[1],
        );
      return {
        key: s.key || `row_${index}`,
        index,
        kind: s.kind,
        snack: isSnack(s),
        label: s.label || KIND_LABELS[s.kind],
        title: `${s.label || KIND_LABELS[s.kind]} · ${s.from}–${s.to}`,
        leftPct: pct(from, start, end),
        widthPct: pct(to, start, end) - pct(from, start, end),
        clippedStart: w[0] < start,
        clippedEnd: w[1] > end,
        clash,
      };
    })
    .filter(Boolean);
};

/** Orders-close ticks for enabled main sittings inside the strip. */
export const dayStripOrderTicks = (
  list,
  start = STRIP_START,
  end = STRIP_END,
) =>
  (Array.isArray(list) ? list : [])
    .filter((s) => isMain(s) && s.enabled !== false && s.orders_close)
    .map((s) => ({ s, m: toMinutes(s.orders_close) }))
    .filter(({ m }) => m !== null && m >= start && m <= end)
    .map(({ s, m }) => ({
      key: s.key,
      leftPct: pct(m, start, end),
      title: `${s.label || KIND_LABELS[s.kind]} orders close ${s.orders_close}`,
    }));
